import { Component } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router';
import XDate from 'xdate';
import StringUtils from '~/common/string-utils';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import ColorUtils from '~/common/color-utils';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { getExpiryData } from '~/components/academies/common/date-utils';
import TruncatedDiv from '../../widget/truncated-div';
import TimeUtils from '../../../common/time-utils';
import { theme } from '../../../styles';
import AcademyBannerUtils from '../../../clc/features/academies/utils/academy-banner-utils';
import academyStyles from './academy-styles';
import AcademyIconSvgGuide from './academy-icon-svg-guide';
import AcademyIconSvgResource from './academy-icon-svg-resource';
import AcademyIconSvgTlc from './academy-icon-svg-tlc';
import AcademyIconSvgEvent from './academy-icon-svg-event';
import AcademyIconSvgCertification from './academy-icon-svg-certification';
import AcademyIconSvgCollection from './academy-icon-svg-collection';

export default class AcademyThemeContentCard extends Component {
  constructor(props) {
    super(props);
    this.state = { name: this.props.name, description: this.props.description };
    if (this.props.contentId) {
      window.collectionCardsUpdaters = window.collectionCardsUpdaters || {};
      window.collectionCardsUpdaters[this.props.contentId] = ({ name, description }) => {
        if (name) {
          this.setState({ name });
        }
        if (description) {
          this.setState({ description });
        }
      };
    }
  }

  handleLinkClick = async (e) => {
    e.preventDefault();
    if (this.props.onClick && !(await this.props.onClick(e))) {
      return;
    }
    return window.open(this.props.url, this.props.target || '_self');
  };

  componentDidUpdate(prevProps) {
    if (!this.props.isAdmin) return;

    if (prevProps.name !== this.props.name) {
      this.setState({ name: this.props.name });
    }
    if (prevProps.description !== this.props.description) {
      this.setState({ description: this.props.description });
    }
  }

  TRANSLATED_FREE = I18n.t('js.academy.content_card.free');

  TRANSLATED_PURCHASED = I18n.t('js.academy.content_card.purchased');

  TRANSLATED_SOLD_OUT = I18n.t('js.academy.content_card.sold_out');

  TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME = StringUtils.getLocalizedTypeFromReadableEnglishType(
    AssetPickerTypes.ACADEMY_CERTIFICATION,
    this.props.contentLabelOverrides,
    true,
  );

  TRANSLATED_PATH_TYPE_DISPLAY_NAME = StringUtils.getLocalizedTypeFromReadableEnglishType(
    AssetPickerTypes.ACADEMY_PATH,
    this.props.contentLabelOverrides,
    true,
  );

  TRANSLATED_EVENT_TYPE_DISPLAY_NAME = StringUtils.getLocalizedTypeFromReadableEnglishType(
    AssetPickerTypes.ACADEMY_EVENT,
    this.props.contentLabelOverrides,
    true,
  );

  TRANSLATED_COLLECTION = I18n.t('common.academy.content.collection');

  render() {
    const {
      accentColor,
      assignmentScore,
      awardedAt,
      badge,
      bannerImage,
      cardStyle,
      children,
      couldAssignmentScoreChange,
      customRenderedBannerImage,
      description,
      ecommerceParams,
      expiresAt,
      flagStyle,
      flagText,
      footerContentType,
      hasAssignment,
      hasCardMetas,
      isAdmin,
      isCollection,
      moreOptions,
      name,
      noBannerImage,
      numContent,
      onClick,
      progressPercent,
      progressText,
      progressTextColor,
      type,
      url,
      target,
      isSpa,
      dueAt,
    } = this.props;
    const { contentCardTypeIcon, contentCardType } = this.getContentType(type);
    // TODO: we'll need to change this and reposition the expiration text if we start charging for recert without
    // making the user create a new certification
    const shouldRenderEcommerceText =
      !expiresAt &&
      !hasAssignment &&
      isFeatureEnabled('ecommerce') &&
      ecommerceParams &&
      ecommerceParams.isEcommerceSetUp;
    const shouldShowScore = assignmentScore != null && progressPercent >= 100;

    const cardFlag = flagText ? (
      <ContentCardFlag flagStyle={flagStyle || 'default'} accentColor={accentColor}>
        {flagText}
      </ContentCardFlag>
    ) : (
      ''
    );

    const { translatedExpiresText, translatedExpiresDate } = getExpiryData(awardedAt, expiresAt, {
      dateFormat: 'medium',
      useShortLabel: true,
    });

    if (children) {
      return (
        <ContentCard
          className={`${academyCustomCSS.cardList.card} ${type ? academyCustomCSS.cardList.cardType[type] : ''} ${this.props.className}`}
        >
          {cardFlag}
          {isSpa ? (
            <Link to={url} onClick={this.handleLinkClick}>
              {children}
            </Link>
          ) : (
            <a href={url} target={target || ''} onClick={this.handleLinkClick}>
              <ContentCardInner>{children}</ContentCardInner>
            </a>
          )}
        </ContentCard>
      );
    }
    const innerContent = (
      <ContentCardInner $isAdmin={isAdmin} className={academyCustomCSS.cardList.cardContentInner}>
        {customRenderedBannerImage || (
          <CollectionBanner
            className={academyCustomCSS.cardList.cardImage}
            $bannerImage={AcademyBannerUtils.getDefaultBannerImage(bannerImage, type)}
            $cardStyle={cardStyle}
            $noBannerImage={noBannerImage}
          >
            {badge && <CollectionBannerBadge badge={badge} noBannerImage={noBannerImage} />}
          </CollectionBanner>
        )}
        <ContentCardContent className={academyCustomCSS.cardList.cardBody}>
          {hasCardMetas && (
            <ContentCardMetas noBannerImage={noBannerImage} className={academyCustomCSS.cardList.cardMetas}>
              {contentCardTypeIcon && (
                <ContentCardIcon className={academyCustomCSS.cardList.cardContentTypeLabel}>
                  {contentCardTypeIcon}
                  <span>{contentCardType}</span>
                </ContentCardIcon>
              )}
              <CardContentMetasRight>{this.getContentMetasRight()}</CardContentMetasRight>
            </ContentCardMetas>
          )}
          <h3 className={academyCustomCSS.cardList.cardTitle}>
            <TruncatedDiv text={this.state.name || ' '} numLines={2} />
          </h3>
          {this.cardDate()}
          <ContentCardContentDescription className={academyCustomCSS.cardList.cardDescription}>
            <TruncatedDiv text={this.state.description || ' '} numLines={3} />
          </ContentCardContentDescription>
        </ContentCardContent>
        <ContentCardFooter
          className={academyCustomCSS.cardList.cardFooter}
          $accentColor={accentColor}
          $progressPercent={progressPercent}
        >
          {!isAdmin && translatedExpiresText && <div>{`${translatedExpiresText}: ${translatedExpiresDate}`}</div>}
          {isCollection && (
            <ContentCardIcon>
              <AcademyIconSvgCollection height={16} color="#1B324B" />
              <span>{this.TRANSLATED_COLLECTION}</span>
            </ContentCardIcon>
          )}
          {shouldRenderEcommerceText && this.renderEcommerceText()}
          {footerContentType && this.getContentCount()}
          {shouldShowScore && this.renderAssignmentScore(assignmentScore, couldAssignmentScoreChange)}
          {progressText && (
            <div style={{ color: progressTextColor }} className={`${academyCustomCSS.cardList.cardCta} progress-text`}>
              {progressText}
            </div>
          )}
        </ContentCardFooter>
      </ContentCardInner>
    );
    return (
      <ContentCard
        className={`${academyCustomCSS.cardList.card} ${type ? academyCustomCSS.cardList.cardType[type] : ''} ${this.props.className}`}
        $isAdmin={isAdmin}
      >
        {cardFlag}
        {this.renderStack(numContent)}
        {isSpa ? (
          <Link to={url} onClick={this.handleLinkClick}>
            {innerContent}
          </Link>
        ) : (
          <a href={url} target={target || ''} onClick={this.handleLinkClick}>
            {innerContent}
          </a>
        )}
        {moreOptions}
      </ContentCard>
    );
  }

  renderEcommerceText = () => {
    const { isSoldOut, doesUserNeedToPurchaseContent, doesContentHavePrice, formattedPrice } =
      this.props.ecommerceParams;
    const soldOutAndNeedsUserPurchase = doesUserNeedToPurchaseContent && isSoldOut;
    let ecommerceText = doesContentHavePrice ? formattedPrice : this.TRANSLATED_FREE;
    if (doesContentHavePrice && !doesUserNeedToPurchaseContent) ecommerceText = this.TRANSLATED_PURCHASED;

    return (
      <EcommerceTextWrapper>
        <EcommerceText $soldOutAndNeedsUserPurchase={soldOutAndNeedsUserPurchase}>{ecommerceText}</EcommerceText>
        {soldOutAndNeedsUserPurchase && <EcommerceTextSoldOut>{this.TRANSLATED_SOLD_OUT}</EcommerceTextSoldOut>}
      </EcommerceTextWrapper>
    );
  };

  renderAssignmentScore = (assignmentScore, couldAssignmentScoreChange) => {
    const TRANSLATED_SCORE = I18n.t('js.academy.content_card.score', { score: assignmentScore });
    const TRANSLATED_CURRENT_SCORE = I18n.t('js.academy.content_card.current_score', { score: assignmentScore });

    return <div className="score-text">{couldAssignmentScoreChange ? TRANSLATED_CURRENT_SCORE : TRANSLATED_SCORE}</div>;
  };

  getContentType = (type) => {
    let contentCardTypeIcon;
    let contentCardType;
    const isPath = this.props.certification ? !this.props.certification.shouldAwardCertification : false;

    if (isFeatureEnabled('scorm_academies') && APP_CONSTANTS.TOP_LEVEL_CONTENT_IN_ACADEMIES.indexOf(type) > -1) {
      contentCardTypeIcon = <AcademyIconSvgTlc height={16} />;
      contentCardType = AssetPickerTypes.getAcademiesDisplayName(type);
    } else {
      switch (type) {
        case AssetPickerTypes.RESOURCE:
          contentCardTypeIcon = <AcademyIconSvgResource height={16} />;
          contentCardType = AssetPickerTypes.getAcademiesDisplayName(AssetPickerTypes.RESOURCE);
          break;
        case AssetPickerTypes.GUIDE:
          contentCardTypeIcon = <AcademyIconSvgGuide height={16} />;
          contentCardType = AssetPickerTypes.getAcademiesDisplayName(AssetPickerTypes.GUIDE);
          break;
        case AssetPickerTypes.ACADEMY_EVENT:
          contentCardTypeIcon = <AcademyIconSvgEvent height={16} />;
          contentCardType = this.TRANSLATED_EVENT_TYPE_DISPLAY_NAME;
          break;
        case AssetPickerTypes.ACADEMY_CERTIFICATION:
          contentCardTypeIcon = <AcademyIconSvgCertification height={16} />;
          contentCardType = isPath
            ? this.TRANSLATED_PATH_TYPE_DISPLAY_NAME
            : this.TRANSLATED_CERTIFICATION_TYPE_DISPLAY_NAME;
          break;
        case AssetPickerTypes.ACADEMY_PATH:
          contentCardTypeIcon = <AcademyIconSvgCertification height={16} />;
          contentCardType = this.TRANSLATED_PATH_TYPE_DISPLAY_NAME;
      }
    }

    return {
      contentCardTypeIcon,
      contentCardType,
    };
  };

  getContentCount = () => {
    const TRANSLATED_NUM_ITEMS = I18n.t('js.academy.content_card.num_item', {
      count: this.props.numContent,
    });

    return <div className="content-text">{TRANSLATED_NUM_ITEMS}</div>;
  };

  getContentMetasRight = () => {
    if (this.props.metasRight) {
      return (
        <StyledCardContentMetasRightTextWrapper className={academyCustomCSS.cardList.cardItemsCount}>
          {this.props.metasRight}
        </StyledCardContentMetasRightTextWrapper>
      );
    }
    if (this.props.timeEstimateSeconds > 0) {
      return this.getTimeEstimate();
    }
  };

  getTimeEstimate = () => (
    <StyledCardContentMetasRightTextWrapper className={academyCustomCSS.cardList.cardItemsCount}>
      {TimeUtils.displayDurationShort(this.props.timeEstimateSeconds)}
    </StyledCardContentMetasRightTextWrapper>
  );

  //  generates stack of blank cards behind collection
  //  that are offset decreasingly from the left, right, vertically
  //  and decrease in opacity
  renderStack = (numberOfStacks) => {
    const stacks = [];
    let currentStackOffset = numberOfStacks;
    for (let i = numberOfStacks; i > 0; i--) {
      stacks.push(
        <Stack
          key={i}
          style={{
            left: `${currentStackOffset}%`,
            right: `${currentStackOffset}%`,
            transform: `translateY(${currentStackOffset}%)`,
            opacity: (100 - currentStackOffset * 15) / 100,
          }}
        />,
      );
      currentStackOffset -= 1;
    }
    return <div className={academyCustomCSS.cardList.cardDecorativeShadow}>{stacks}</div>;
  };

  cardDate = () => {
    const { dueAt } = this.props;

    if (dueAt) {
      const xDate = new XDate(dueAt);
      const jsDate = xDate.toDate();
      const formattedDate = I18n.l('date.formats.short', jsDate);
      const dueLabel = I18n.t('js.learner.card.due');
      const TRANSLATED_DATE_STRING = `${dueLabel} ${formattedDate}`;

      return (
        <ContentCardContentDueDate>
          <TruncatedDiv text={TRANSLATED_DATE_STRING} numLines={1} />
        </ContentCardContentDueDate>
      );
    }

    return null;
  };
}

const ContentCard = styled.div`
  transition: all 0.3s ease-in-out;
  margin-bottom: 4rem;
  position: relative;
  width: ${academyStyles.card.cardWidth}px;
  background-color: #fff;
  border-radius: 6px;
  cursor: pointer;
  ${(props) =>
    props.$isAdmin &&
    `
    width: 100%;
    margin: 0;
  `}
  a {
    text-decoration: none;
    &:hover {
      text-decoration: none;
    }
    &[href='javascript:void(0);'] {
      cursor: default;
    }
  }
  @media (max-width: 420px) {
    width: 100%;
  }
`;

const ContentCardInner = styled.div`
  position: relative;
  box-shadow: 0 4px 24px 0 rgba(161, 179, 200, 0.25);
  transition: all 0.3s ease-in-out;
  border-radius: 6px;
  background-color: #fff;
  &:hover {
    box-shadow: 0 3px 25px rgba(161, 179, 200, 0.7);
    ${(props) => !props.$isAdmin && 'transform: translateY(-1px);'}
  }
  .editable-image {
    > div {
      height: 0;
      padding-bottom: 56.25%;
    }
    label {
      height: 100%;
    }
  }
`;

const CollectionBanner = styled.div`
  height: 0;
  padding-bottom: 56.25%;
  background: url(${(props) => props.$bannerImage}) center center no-repeat;
  background-size: cover;
  border-radius: 6px 6px 0 0;
  box-sizing: border-box;
  position: relative;
  ${(props) => props.$cardStyle === 'bordered' && !props.$noBannerImage && 'border: solid 40px #eee;'}
  ${(props) =>
    props.$noBannerImage &&
    `
    background: none;
    padding-bottom: 0;
    height: 125px;
  `}
`;

const ContentCardFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 25px;
  right: 25px;
  box-sizing: border-box;
  padding: 15px 0;
  border-top: solid 1px #d8d8d8;
  font-size: 0.875rem;
  color: #697788;
  height: 48px;
  cursor: default;
  display: flex;
  justify-content: space-between;
  ${theme.font.semibold}
  & > div:last-child {
    margin-left: auto;
  }
  ${(props) =>
    props.$progressPercent &&
    `
      border: none;
      &:before, &:after {
        display: block;
        content: "";
        position: absolute;
        height: 8px;
        top: -4px;
        left: 0;
      }
      &:before {
        width: 100%;
        background-color: #EDEDEF;
        border-radius: 4px;
      }
      &:after {
        width: ${props.$progressPercent}%;
        background-color: ${props.$accentColor || '#286EF3'};
        border-radius: ${props.$progressPercent === 100 ? '4px' : '4px 0 0 4px'};
      }
    `}
`;

const ContentCardContent = styled.div`
  height: 250px;
  padding: 0 25px 48px;
  box-sizing: border-box;
  border-radius: 0 0 6px 6px;
  background-color: #fff;
  position: relative;
  h3 {
    font-size: 1.5rem;
    color: #1b324b;
    margin-top: 20px;
    margin-bottom: 5px;
    ${theme.font.bold}
  }
`;

const ContentCardContentDescription = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme: propsTheme }) => propsTheme.colors.on[600]};
  margin-top: 5px;
  ${theme.font.regular}
`;

const ContentCardContentDueDate = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(27, 50, 75, 0.8);
  ${theme.font.regular}
`;

const ContentCardMetas = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
  margin-bottom: 20px;
  ${(props) => props.noBannerImage && 'margin-top: 0;'}
`;

const CardContentMetasRight = styled.div`
  color: ${({ theme: propsTheme }) => propsTheme.colors.on[600]};
  font-size: 0.6875rem;
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const StyledCardContentMetasRightTextWrapper = styled.div`
  width: 100%;
  text-align: right;
`;

const ContentCardIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme: propsTheme }) => propsTheme.colors.on[600]};
  span {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: inline-block;
    margin-left: 10px;
  }
`;

const Stack = styled.div`
  background-color: #fff;
  box-shadow: 0 3px 5px rgba(161, 179, 200, 0.23);
  height: 100%;
  position: absolute;
  top: 0;
  border-radius: 6px;
  z-index: 0;
`;

const EcommerceTextWrapper = styled.div.attrs({
  className: 'progress-text',
})`
  display: flex;
`;

const EcommerceText = styled.div`
  text-decoration: ${(props) => (props.$soldOutAndNeedsUserPurchase ? 'line-through' : '')};
`;

const EcommerceTextSoldOut = styled.div`
  color: red;
  margin-left: 10px;
`;

const ContentCardFlag = styled.div`
  position: absolute;
  left: -5px;
  top: 10px;
  border-radius: 4px;
  font-size: 0.7rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  padding: 4px 8px;
  z-index: 10;

  ${(props) =>
    props.flagStyle === 'default' &&
    `
      color: ${props.accentColor ? ColorUtils.getTextColorForHex(props.accentColor) : '#fff'};
      background-color: ${props.accentColor || theme.color.overdue};
    `}
`;

const CollectionBannerBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: url(${(props) => props.badge}) center center no-repeat;
  background-size: cover;
  ${(props) =>
    props.noBannerImage &&
    `
    width: 60px;
    height: 60px;
    left: 25px;
    transform: translateY(-50%);
  `}
`;
